import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './BlogListPage.css';
import { blogsByDate } from '../data/blogs';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const BlogListPage = () => {
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState('All');

  const allTags = useMemo(() => {
    const set = new Set();
    blogsByDate.forEach((b) => b.tags?.forEach((t) => set.add(t)));
    return ['All', ...Array.from(set)];
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return blogsByDate.filter((b) => {
      const matchesTag = activeTag === 'All' || (b.tags || []).includes(activeTag);
      if (!q) return matchesTag;
      const haystack = [
        b.title,
        b.subtitle || '',
        b.excerpt,
        b.author,
        ...(b.tags || []),
      ]
        .join(' ')
        .toLowerCase();
      return matchesTag && haystack.includes(q);
    });
  }, [query, activeTag]);

  return (
    <main className="blog-list-page">
      <section className="blog-list-hero">
        <div className="container">
          <p className="blog-list-eyebrow">Knowledge & Reflection</p>
          <h1 className="blog-list-title">The Jasar Institute Blog</h1>
          <p className="blog-list-subtitle">
            Articles on Quran, Hadith, spirituality and the practical aspects of
            Islam — written to make timeless wisdom accessible to modern readers.
          </p>
        </div>
      </section>

      <section className="blog-list-content">
        <div className="container">
          <div className="blog-list-controls">
            <div className="blog-search">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="blog-search-icon"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles by title, author or topic"
                className="blog-search-input"
                aria-label="Search articles"
              />
              {query && (
                <button
                  type="button"
                  className="blog-search-clear"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>

            <div className="blog-tags" role="tablist" aria-label="Filter by topic">
              {allTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  role="tab"
                  aria-selected={activeTag === tag}
                  className={`blog-tag-chip ${activeTag === tag ? 'active' : ''}`}
                  onClick={() => setActiveTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="blog-empty">
              <div className="blog-empty-icon">📭</div>
              <h3>No articles match your search</h3>
              <p>Try a different keyword or topic.</p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setQuery('');
                  setActiveTag('All');
                }}
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="blog-list-grid">
              {filtered.map((blog) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.id}`}
                  className="blog-list-card"
                  aria-label={`Read: ${blog.title}`}
                >
                  <div
                    className="blog-list-card-cover"
                    style={{ background: blog.coverGradient }}
                  >
                    <span className="blog-list-card-icon" aria-hidden="true">
                      {blog.coverIcon}
                    </span>
                    <div className="blog-list-card-overlay" />
                    {blog.tags?.[0] && (
                      <span className="blog-list-card-tag">{blog.tags[0]}</span>
                    )}
                  </div>

                  <div className="blog-list-card-body">
                    <div className="blog-list-card-meta">
                      <span>{formatDate(blog.date)}</span>
                      <span aria-hidden="true">•</span>
                      <span>{blog.readTime} min read</span>
                    </div>
                    <h3 className="blog-list-card-title">{blog.title}</h3>
                    {blog.subtitle && (
                      <p className="blog-list-card-subtitle">{blog.subtitle}</p>
                    )}
                    <p className="blog-list-card-excerpt">{blog.excerpt}</p>
                    <div className="blog-list-card-footer">
                      <span className="blog-list-card-author">By {blog.author}</span>
                      <span className="blog-list-card-cta">
                        Read
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default BlogListPage;
